/**
 * Forge subscription hooks — thin adapters over `@ezhard/react`.
 *
 * The live-subscription machinery now lives in the shared package; the only
 * thing kept here is EZHaul's convention that a document's `id` is part of its
 * data. The engine returns the id on the snapshot (`snapshot.id` / `doc.id`),
 * never inside `data()`, and every EZHaul model type declares `id: string`
 * (routing, list keys and deletes all read it), so the id is merged back in
 * here rather than at ~25 call sites.
 */
import { useMemo } from 'react';
import {
  useForgeDoc as useForgeDocBase,
  useForgeCollection as useForgeCollectionBase,
} from '@ezhard/react';
import type { UseCollectionResult, UseDocResult } from '@ezhard/react';
import type { QueryConstraint } from '@ezhard/sdk';

export type UseForgeDocResult<T> = UseDocResult<T>;
export type UseForgeCollectionResult<T> = UseCollectionResult<T>;

/** Live document subscription. Pass `null` to subscribe conditionally. */
export function useForgeDoc<T = Record<string, unknown>>(
  path: string | null,
): UseForgeDocResult<T> {
  const result = useForgeDocBase<T>(path);
  const { snapshot, exists, data: raw } = result;

  const data = useMemo<T | null>(() => {
    if (!snapshot || !exists) return null;
    return { id: snapshot.id, ...((raw as Record<string, unknown> | null) ?? {}) } as T;
  }, [snapshot, exists, raw]);

  return { ...result, data };
}

/** Live query subscription. Pass `null` to subscribe conditionally. */
export function useForgeCollection<T = Record<string, unknown>>(
  collectionPath: string | null,
  constraints: QueryConstraint[] = [],
): UseForgeCollectionResult<T> {
  const result = useForgeCollectionBase<T>(collectionPath, constraints);
  const { docs } = result;

  const data = useMemo<T[]>(
    () => docs.map((doc) => ({ id: doc.id, ...(doc.data() ?? {}) }) as T),
    [docs],
  );

  return { ...result, data };
}
